/* Human-quotable client IDs.
   ---------------------------------------------------------------------------
   A client's only identifier was the Firebase uid — 28 opaque characters that
   nobody can read down a phone line or write on an invoice. This turns that uid
   into a short code, `CNC-4K7M-2PQ9`, for admin screens to show and staff to
   quote.

   Derived, not stored, and that is the point:
     · every existing client has one immediately — no migration, no counter, no
       backend write, nothing to keep in sync;
     · the same uid always produces the same code, on every screen and in every
       session, because there is no state involved;
     · `_id` and `uid` are the same value in the admin payload (auth.js keeps
       them in sync deliberately), so any admin screen derives the same code.

   The trade-off is that the codes are not sequential — CNC-4K7M-2PQ9 says
   nothing about signup order. If you ever want "client #42", that needs a
   stored counter; this is the version that works today for everyone. */

type AnyObj = Record<string, any>;

/** Crockford's base32: no I, L, O or U, so nothing reads as 1 / 0 or a swearword. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export const CLIENT_ID_PREFIX = 'CNC';

/** cyrb53 — small, dependency-free, well-distributed 53-bit string hash. */
function cyrb53(str: string, seed = 0x9e3779b1): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

function base32(n: number, chars: number): string {
  let out = '';
  let v = Math.abs(Math.trunc(n));
  for (let i = 0; i < chars; i++) {
    out = (ALPHABET[v % 32] ?? '0') + out;
    v = Math.floor(v / 32);
  }
  return out;
}

/** The uid a client record is keyed on, whichever field the caller has. */
export const uidOf = (u: AnyObj | string | null | undefined): string =>
  typeof u === 'string' ? u.trim() : String(u?.uid || u?._id || u?.id || '').trim();

/**
 * The client's ID, e.g. `CNC-4K7M-2PQ9`.
 *
 * Eight base32 characters — a 40-bit space, so a collision inside a book of
 * even tens of thousands of clients stays in the thousandths of a percent.
 * Returns '' when there is no uid to derive from, so callers can render a dash.
 */
export function clientId(u: AnyObj | string | null | undefined): string {
  const uid = uidOf(u);
  if (!uid) return '';
  const code = base32(cyrb53(uid), 8);
  return `${CLIENT_ID_PREFIX}-${code.slice(0, 4)}-${code.slice(4)}`;
}

/** Letters and digits only, upper case — so search ignores dashes and case. */
const squash = (s: string) => s.replace(/[^0-9a-z]/gi, '').toUpperCase();

/**
 * Does this client match a typed/pasted ID? Tolerates `cnc-4k7m-2pq9`,
 * `CNC4K7M2PQ9` and a bare `4K7M2PQ9`, and also matches on the raw uid so a
 * paste from Firebase still finds the person.
 */
export function clientIdMatches(u: AnyObj | string | null | undefined, query: string): boolean {
  const needle = squash(query);
  if (needle.length < 3) return false;
  return squash(clientId(u)).includes(needle) || uidOf(u).toUpperCase().includes(needle);
}
